import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { getSocket } from '@/utils/functions';

declare module '@tanstack/react-query' {
    interface Register {
        mutationMeta: { syncQueryKey?: QueryKey };
    }
}

type EventHandler<TData, TEvent> = {
    update: (cached: TData, event: TEvent) => TData;
    skip?: (event: TEvent) => boolean;
};

interface SyncedChannelOptions<TData, TEvent> {
    queryKey: QueryKey;
    channel: string;
    events: Record<string, EventHandler<TData, TEvent>>;
    token: string;
    enabled?: boolean;
    skipOwn?: boolean;
    getUserId?: (event: TEvent) => number;
    onEvent?: (event: TEvent) => void;
}

function serializeKey(key: QueryKey): string {
    return JSON.stringify(key);
}

export function useSyncedChannel<TData, TEvent = unknown>(options: SyncedChannelOptions<TData, TEvent>): void {
    const { queryKey, channel, events, token, enabled = true, getUserId, onEvent } = options;

    const queryClient = useQueryClient();
    const eventsRef = useRef(events);
    eventsRef.current = events;
    const onEventRef = useRef(onEvent);
    onEventRef.current = onEvent;
    const getUserIdRef = useRef(getUserId);
    getUserIdRef.current = getUserId;

    const serializedKey = serializeKey(queryKey);

    useEffect(() => {
        if (!enabled || !token) return;

        const buffer: { eventName: string; payload: TEvent }[] = [];
        const inflight = new Set<number>();

        // Track in-flight mutations for our query key
        const unsubscribe = queryClient.getMutationCache().subscribe(event => {
            if (!event.mutation) return;

            const meta = event.mutation.options.meta;
            if (!meta?.syncQueryKey || serializeKey(meta.syncQueryKey) !== serializedKey) return;

            if (event.type === 'updated') {
                const state = event.mutation.state;
                if (state.status === 'pending') {
                    inflight.add(event.mutation.mutationId);
                } else {
                    // settled (success or error) — remove and flush
                    inflight.delete(event.mutation.mutationId);
                    if (inflight.size === 0 && buffer.length > 0) {
                        flushBuffer();
                    }
                }
            }
        });

        function flushBuffer() {
            const events = eventsRef.current;
            while (buffer.length > 0) {
                const item = buffer.shift();
                if (!item) break;
                const handler = events[item.eventName];
                if (handler) {
                    queryClient.setQueryData<TData>(queryKey, old => {
                        if (old === undefined) return old;
                        return handler.update(old, item.payload);
                    });
                }
            }
        }

        function handleEvent(eventName: string, payload: TEvent) {
            const handler = eventsRef.current[eventName];
            if (!handler) return;

            if (handler.skip?.(payload)) return;

            onEventRef.current?.(payload);

            if (inflight.size > 0) {
                buffer.push({ eventName, payload });
                return;
            }

            queryClient.setQueryData<TData>(queryKey, old => {
                if (old === undefined) return old;
                return handler.update(old, payload);
            });
        }

        const socket = getSocket(token);
        const ch = socket.private(channel);

        for (const eventName of Object.keys(eventsRef.current)) {
            if (eventName === 'notification') {
                // Laravel Echo .notification() method for broadcast notifications
                ch.notification((payload: TEvent) => handleEvent('notification', payload));
            } else {
                ch.listen(eventName, (payload: TEvent) => handleEvent(eventName, payload));
            }
        }

        return () => {
            unsubscribe();
            socket.disconnect();
        };
    }, [serializedKey, channel, token, enabled, queryClient, queryKey]);
}
